import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Routes, Route, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { IntlProvider } from "react-intl";

import { deviceType, loadStripeScript } from "@lib/utils";
import { HeaderFull } from "@widgets";
import { Button } from "@ui-kit";
import { Snackbar } from "@features";
import { setCurrentProjectAC } from "@entities/project/model/project.actions";
import { getCurrentProjectSC } from "@entities/project/model/project.selectors";
import { getSettingsTC } from "@entities/settings/model/settings.actions";
import { setAlertAC } from "@entities/notifications/model/notifications.actions";
import { Snackbar as SnackbarClass, projectInstance } from "@entities/dtos";
import { usersGetExecutorsTC } from "@entities/users/model/users.actions";
import { ROLES } from "@entities/user/model/user.constants";
import {
  userIsAuthSC,
  userIsOnlineSC,
  userGetCartSC,
  userGetCustomerSC,
  userGetLocaleSC,
} from "@entities/user/model/user.selectors";
import {
  userGetPaidSubscribeTC,
  userGetStripeSettingsTC,
  userSetDeviceTypeAC,
  userSetLocaleAC,
  userSetModalLoginShowAC,
  userSetNetworkAC,
  userSetUserAC,
} from "@entities/user/model/user.actions";
import {
  STATIC_ROUTES,
  ADMIN_ROUTES,
  EXECUTOR_ROUTES,
  SUPER_ADMIN_ROUTES,
  USERS_ROUTES,
  AUTH_ROUTES,
} from "@app/routes";
import { Socket } from "@shared";
import { UserContext, PopoverProvider } from "@shared/hooks";
import rusTranslation from "@shared/lib/languages/russian.json";
import engTranslation from "@shared/lib/languages/english.json";

import ProtectedRouteComponent from "@app/routes/protected-route";

const AuthSplitter = () => {
  const isUserAuth = useSelector(userIsAuthSC);
  const isUserOnline = useSelector(userIsOnlineSC);
  const user = useSelector(userGetCustomerSC);
  const currentProject = useSelector(getCurrentProjectSC);
  const cart = useSelector(userGetCartSC);
  const locale = useSelector(userGetLocaleSC);
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const urlLang = searchParams.get("lang");

  useEffect(() => {
    dispatch(userSetDeviceTypeAC(deviceType()));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (urlLang) {
      dispatch(userSetLocaleAC(urlLang));
    }
    // eslint-disable-next-line
  }, [urlLang]);

  const language = locale === "ru" ? rusTranslation : engTranslation;

  useEffect(() => {
    if (isUserAuth) {
      dispatch(userSetModalLoginShowAC(false));
    }
    // eslint-disable-next-line
  }, [isUserAuth]);

  const setSuccessConnection = () => {
    dispatch(userSetNetworkAC(true));
  };

  const setFailConnection = () => {
    dispatch(userSetNetworkAC(false));
  };

  const setCloseConnection = () => {
    dispatch(userSetNetworkAC(false));
  };

  /* SOCKET CONNECTION */
  function listenerSetCurrent(event) {
    const data = event?.data;
    if (!data) return;

    try {
      if (event.event === `model.user.afterSave`) {
        const savedUser = event?.data;

        if (savedUser?.id === user.id) {
          dispatch(userSetUserAC(Object.assign({}, user, savedUser)));
        }
      }

      if (
        event.event === `model.project.afterSave` &&
        event?.data.id === currentProject.id
      ) {
        const projectType =
          user.role === ROLES.USER ? "userProject" : "adminProject";
        const updatedProjectDTO = projectInstance.create(
          event?.data,
          projectType,
        );

        const updatedProject = Object.keys(event?.data).reduce(
          (result, key) => {
            result[key] = updatedProjectDTO[key];

            return result;
          },
          {},
        );

        dispatch(
          setCurrentProjectAC(
            Object.assign({}, currentProject, updatedProject),
          ),
        );
      }
    } catch (e) {
      console.error("Error parsing message:", e);
    }
  }

  useEffect(() => {
    if (!user.id) {
      return;
    }

    if (checkRole(ROLES.ADMIN)) {
      dispatch(usersGetExecutorsTC());
      dispatch(getSettingsTC());
    }

    if (checkRole(ROLES.EXECUTOR)) {
      dispatch(usersGetExecutorsTC());
    }

    loadStripeScript().then(() => {
      dispatch(userGetStripeSettingsTC());
    });

    dispatch(userGetPaidSubscribeTC());

    const notifyUserError = (header, message) => {
      const snackbar = new SnackbarClass({
        status: 205,
        duration: Infinity,
        message: (
          <div className="">
            <h5 className="snack__header">{header}</h5>
            <div className="snack__text">{message}</div>
            <div className="snack__actions">
              <Button
                theme="link-color"
                size="sm"
                onClick={() => window.location.reload()}
              >
                Update
              </Button>
            </div>
          </div>
        ),
      });

      dispatch(setAlertAC(snackbar));
    };

    try {
      window.socket = new Socket(user.id, user.role);
      window.socket.initListeners(
        setSuccessConnection,
        setFailConnection,
        setCloseConnection,
      );

      window.socket.listen(listenerSetCurrent);

      const socketCloseEventListener = () => {
        notifyUserError(
          "Failed to reconnect",
          "Please refresh the page to restore your connection.",
        );
      };

      window.socket.onReconnectError = socketCloseEventListener;
    } catch (error) {
      console.error("[Socket] Init error:", error);
    }

    return () => {
      if (window?.socket) {
        window.socket.stopListening(listenerSetCurrent);
        //window.socket.close();
      }
    };

    // eslint-disable-next-line
  }, [user.id]);

  const renderRout = (route, index) => {
    if (route.internalsRouts) {
      return (
        <Route key={`route-${index}`} path={route.path.pathname}>
          {route.internalsRouts.map((internalRoute, index) => (
            <Route
              key={`internalsRouts-${index}`}
              path={internalRoute.path.pathname}
              element={internalRoute.element}
            />
          ))}
        </Route>
      );
    }

    return (
      <Route
        key={index}
        path={route.path.pathname}
        element={route.element}
        exact={route.exact}
        sitemapIndex={route.sitemapIndex}
      />
    );
  };

  const checkRole = (role) => {
    return user && user.role === role;
  };

  let ROUTER_BUILDER = [...STATIC_ROUTES];

  if (isUserAuth) {
    if (checkRole(ROLES.USER)) {
      ROUTER_BUILDER = [...ADMIN_ROUTES, ...USERS_ROUTES, ...ROUTER_BUILDER];
    }

    if (checkRole(ROLES.EXECUTOR)) {
      ROUTER_BUILDER = [...ADMIN_ROUTES, ...EXECUTOR_ROUTES, ...ROUTER_BUILDER];
    }

    if (checkRole(ROLES.ADMIN)) {
      ROUTER_BUILDER = [
        ...ADMIN_ROUTES,
        ...EXECUTOR_ROUTES,
        ...SUPER_ADMIN_ROUTES,
        ...ROUTER_BUILDER,
      ];
    }
  } else {
    const PROTECTED_ROUTES = [
      ...ADMIN_ROUTES,
      ...USERS_ROUTES,
      ...EXECUTOR_ROUTES,
      ...SUPER_ADMIN_ROUTES,
    ];

    PROTECTED_ROUTES.forEach((route) => {
      [route].concat(route.internalsRouts || []).forEach((_route) => {
        _route.element = (
          <ProtectedRouteComponent>{_route.element}</ProtectedRouteComponent>
        );
      });
    });

    ROUTER_BUILDER = [...PROTECTED_ROUTES, ...ROUTER_BUILDER, ...AUTH_ROUTES];
  }

  const broadenUser = {
    ...user,
    isUserAuth,
    isUserOnline,
    cart,
  };
  // notifications,

  return (
    <>
      <Helmet
        defaultTitle="Stock Images, Vectors, Illustrations | FlowPik"
        meta={[
          {
            name: "keywords",
            content: "image, illustration, vector, order, brief, stock,",
          },
          {
            name: "description",
            content: "FlowPik is here instead of searching",
          },
        ]}
        link={[
          {
            rel: "icon",
            href: "/favicon.ico",
            type: "image/x-icon",
            sizes: "any",
          },
          { rel: "icon", href: "/logo.svg", type: "image/svg+xml" },
          { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
          { rel: "manifest", href: "/manifest.json" },
        ]}
      />

      <div className="main__wrapper">
        <PopoverProvider>
          <UserContext.Provider value={broadenUser}>
            <Snackbar />
            <HeaderFull />
          </UserContext.Provider>
          <div className="main__content" id="main__content">
            <IntlProvider
              messages={language}
              locale={locale}
              defaultLocale="en"
            >
              <Routes>
                {ROUTER_BUILDER.map((route, index) => renderRout(route, index))}
              </Routes>
            </IntlProvider>
          </div>
        </PopoverProvider>
      </div>
    </>
  );
};

export default AuthSplitter;
